export type FetchBaseQueryError = {
  status: number | string;
  data?: unknown;
  error?: string;
  originalStatus?: number;
  response?: unknown;
  [key: string]: unknown;
};

export type FetchArgs = {
  url: string;
  method?: string;
  body?: unknown;
  headers?: HeadersInit;
  params?: Record<string, string | number | boolean | undefined>;
};

export type BaseQueryApi = {
  endpoint: string;
};

export type BaseQueryFn = (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions?: unknown
) => Promise<{ data?: unknown; error?: FetchBaseQueryError }>;

type TPrepareHeaders = (headers: Headers) => Headers | void | Promise<Headers | void>;

type TFetchBaseQueryArgs = {
  baseUrl?: string;
  timeout?: number;
  prepareHeaders?: TPrepareHeaders;
};

function buildRequestUrl(baseUrl: string, url: string) {
  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  if (!baseUrl) {
    return url;
  }

  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = url.startsWith('/') ? url.slice(1) : url;

  return `${normalizedBase}/${normalizedPath}`;
}

function appendParams(url: string, params?: Record<string, string | number | boolean | undefined>) {
  if (!params) {
    return url;
  }

  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (typeof value !== 'undefined') {
      query.append(key, String(value));
    }
  });

  const queryString = query.toString();

  if (!queryString) {
    return url;
  }

  return `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
}

function parseResponseText(responseText: string, contentType: string): { data?: unknown; parsingError?: string } {
  if (responseText.length === 0) {
    return { data: null };
  }

  if (contentType.includes('application/json')) {
    try {
      return { data: JSON.parse(responseText) as unknown };
    } catch (_error) {
      return {
        data: responseText,
        parsingError: 'Failed to parse response body.',
      };
    }
  }

  try {
    return { data: JSON.parse(responseText) as unknown };
  } catch (_error) {
    return { data: responseText };
  }
}

export function fetchBaseQuery({ baseUrl = '', timeout = 600000, prepareHeaders }: TFetchBaseQueryArgs): BaseQueryFn {
  return async (args: string | FetchArgs, _api: BaseQueryApi, _extraOptions?: unknown) => {
    const request = typeof args === 'string' ? ({ url: args, method: 'GET' } as FetchArgs) : args;
    const method = (request.method ?? 'GET').toUpperCase();
    const requestUrl = appendParams(buildRequestUrl(baseUrl, request.url), request.params);

    let headers = new Headers(request.headers);
    if (prepareHeaders) {
      const updatedHeaders = await prepareHeaders(headers);
      if (updatedHeaders instanceof Headers) {
        headers = updatedHeaders;
      }
    }

    let body: BodyInit | undefined;

    if (typeof request.body !== 'undefined' && method !== 'GET' && method !== 'HEAD') {
      if (
        request.body instanceof FormData ||
        request.body instanceof Blob ||
        request.body instanceof URLSearchParams ||
        typeof request.body === 'string'
      ) {
        body = request.body;
      } else {
        if (!headers.has('content-type')) {
          headers.set('content-type', 'application/json');
        }
        body = JSON.stringify(request.body);
      }
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => {
      controller.abort();
    }, timeout);

    try {
      const response = await fetch(requestUrl, {
        method,
        headers,
        body,
        signal: controller.signal,
      });

      const responseText = await response.text();
      const { data, parsingError } = parseResponseText(responseText, response.headers.get('content-type') ?? '');

      if (parsingError) {
        return {
          error: {
            status: 'PARSING_ERROR',
            originalStatus: response.status,
            data,
            error: parsingError,
          },
        };
      }

      if (!response.ok) {
        return {
          error: {
            status: response.status,
            data,
          },
        };
      }

      return { data };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch';
      return {
        error: {
          status: 'FETCH_ERROR',
          error: message,
        },
      };
    } finally {
      window.clearTimeout(timeoutId);
    }
  };
}
